import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  Globe2,
  Layers3,
  Rocket,
  ShieldCheck,
  Sparkles,
  Zap,
} from "lucide-react";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import SEO, { SITE_URL } from "../SEO/SEO";
import "./AboutUs.scss";

const milestones = [
  {
    label: "Founded",
    value: "05 May 2025",
    copy: "Teleport Now begins with one mission: rebuild travel around speed, simplicity, and a unified app experience.",
  },
  {
    label: "Built By",
    value: "30+ years",
    copy: "Our founders and developers bring combined experience across eCommerce, social platforms, scale, and user-centric product design.",
  },
  {
    label: "Licensed",
    value: "Singapore Tourism Board",
    copy: "Official Singapore licensing supports our long-term commitment to trust, compliance, and global credibility.",
  },
];

const principles = [
  {
    icon: Zap,
    title: "Speed Over Legacy",
    copy: "Travel should not be slowed down by outdated systems, fragmented tools, or processes that were built for another era.",
  },
  {
    icon: Layers3,
    title: "One Platform",
    copy: "Connectivity, bookings, airport experiences, in-destination services, rewards, and support belong in one seamless travel companion.",
  },
  {
    icon: Sparkles,
    title: "Built Around Users",
    copy: "We apply the pace and product thinking of modern digital ecosystems to make travel feel instant, intuitive, and easier to trust.",
  },
];

const journeyStages = [
  {
    number: "01",
    title: "Plan with less friction",
    copy: "Compare essentials and make informed choices without rebuilding the same trip across disconnected services.",
  },
  {
    number: "02",
    title: "Stay connected as you move",
    copy: "Keep data, booking details, rewards, and travel support within reach before departure and while abroad.",
  },
  {
    number: "03",
    title: "Keep every journey together",
    copy: "One Teleport Now account connects the services you use today with the travel experiences we are building next.",
  },
];

const ecosystem = [
  "Travel eSIMs",
  "Flights",
  "Hotels",
  "Airport Experiences",
  "Rides",
  "Attractions",
  "Rewards",
  "Trip Support",
];

const aboutFaqs = [
  {
    question: "What is Teleport Now?",
    answer:
      "Teleport Now is a Singapore travel technology app built to unify travel eSIMs, bookings, airport experiences, in-destination services, rewards, and trip support in one platform.",
  },
  {
    question: "When was Teleport Now founded?",
    answer:
      "Teleport Now was founded on 5 May 2025 with the ambition to become one app for all travel needs.",
  },
  {
    question: "Is Teleport Now licensed in Singapore?",
    answer:
      "Teleport Now is officially licensed under the Singapore Tourism Board and the Infocomm Media Development Authority.",
  },
  {
    question: "What does All you need to go mean?",
    answer:
      "All you need to go is Teleport Now's product philosophy for making travel faster, simpler, and more seamless across connectivity, bookings, deals, rewards, and support.",
  },
];

const revealUp = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: "easeOut" },
  },
};

const staggerChildren = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const AboutUs = () => {
  return (
    <div className="aboutus">
      <SEO
        title="About Teleport Now | All You Need To Go"
        description="Teleport Now was founded on 5 May 2025 to reinvent travel as one seamless app for eSIMs, bookings, airport experiences, in-destination services, rewards, and support."
        canonicalPath="/about-us"
        keywords="Teleport Now about us, travel app Singapore, all in one travel app, travel eSIM app, STB licensed travel app, IMDA licensed travel app"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "AboutPage",
              "@id": `${SITE_URL}/about-us#webpage`,
              url: `${SITE_URL}/about-us`,
              name: "About Teleport Now",
              description:
                "Teleport Now is a Singapore travel technology company building one app for all travel needs.",
              isPartOf: {
                "@id": `${SITE_URL}/#website`,
              },
              about: {
                "@id": `${SITE_URL}/#organization`,
              },
              primaryImageOfPage: {
                "@type": "ImageObject",
                url: `${SITE_URL}/images/hero-futuristic.jpeg`,
              },
              breadcrumb: {
                "@id": `${SITE_URL}/about-us#breadcrumb`,
              },
            },
            {
              "@type": "Organization",
              "@id": `${SITE_URL}/#organization`,
              name: "Teleport Now",
              url: SITE_URL,
              logo: `${SITE_URL}/images/Android.png`,
              foundingDate: "2025-05-05",
              foundingLocation: {
                "@type": "Place",
                name: "Singapore",
                address: {
                  "@type": "PostalAddress",
                  addressCountry: "SG",
                },
                geo: {
                  "@type": "GeoCoordinates",
                  latitude: 1.3521,
                  longitude: 103.8198,
                },
              },
              slogan: "All you need to go.",
              description:
                "Teleport Now is building one travel app for eSIMs, bookings, airport experiences, in-destination services, rewards, and support.",
              areaServed: [
                { "@type": "Country", name: "Singapore" },
                { "@type": "Place", name: "Southeast Asia" },
                { "@type": "Place", name: "Asia Pacific" },
                { "@type": "Place", name: "Worldwide" },
              ],
              knowsAbout: [
                "travel eSIMs",
                "flight booking",
                "hotel booking",
                "travel deals",
                "airport experiences",
                "travel rewards",
                "trip support",
                "travel technology",
              ],
              sameAs: [
                "https://apps.apple.com/sg/app/teleport-now/id6756201717",
                "https://play.google.com/store/apps/details?id=sg.teleportnow.app",
              ],
            },
            {
              "@type": "SoftwareApplication",
              "@id": `${SITE_URL}/#app`,
              name: "Teleport Now",
              applicationCategory: "TravelApplication",
              operatingSystem: "iOS, Android",
              url: `${SITE_URL}/download`,
              featureList: ecosystem,
              publisher: {
                "@id": `${SITE_URL}/#organization`,
              },
            },
            {
              "@type": "OfferCatalog",
              "@id": `${SITE_URL}/about-us#travel-ecosystem`,
              name: "Teleport Now travel ecosystem",
              itemListElement: ecosystem.map((name) => ({
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name,
                  provider: {
                    "@id": `${SITE_URL}/#organization`,
                  },
                },
              })),
            },
            {
              "@type": "FAQPage",
              "@id": `${SITE_URL}/about-us#faq`,
              mainEntity: aboutFaqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: faq.answer,
                },
              })),
            },
            {
              "@type": "BreadcrumbList",
              "@id": `${SITE_URL}/about-us#breadcrumb`,
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: SITE_URL,
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "About Teleport Now",
                  item: `${SITE_URL}/about-us`,
                },
              ],
            },
          ],
        }}
      />

      <Navbar />

      <main>
        <section className="about-hero">
          <div
            className="about-hero__media"
            style={{ backgroundImage: 'url("/images/hero-futuristic.jpeg")' }}
            aria-hidden="true"
          />
          <div className="about-hero__overlay" />
          <div className="about-hero__grid" aria-hidden="true" />
          <div className="about-hero__scan" aria-hidden="true" />
          <div className="about-hero__inner">
            <motion.div
              className="about-hero__copy"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
            >
              <p className="about-eyebrow">Founded on 5 May 2025</p>
              <h1>Travel should feel connected from the very first step.</h1>
              <p>
                Teleport Now is building a simpler way to plan, book, connect,
                and manage a journey. We bring essential travel services into
                one thoughtful experience designed around real travellers.
              </p>
            </motion.div>

            <motion.aside
              className="about-hero__system"
              initial={{ opacity: 0, x: 28 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.12, duration: 0.55 }}
              aria-label="Teleport Now travel ecosystem"
            >
              <p>All you need to go.</p>
              <div className="about-hero__route" aria-hidden="true">
                <span />
                <span />
                <span />
              </div>
              <ul>
                <li><strong>Before you go</strong><span>Search, compare, and prepare.</span></li>
                <li><strong>While you travel</strong><span>Stay connected and supported.</span></li>
                <li><strong>After every trip</strong><span>Keep rewards and travel history together.</span></li>
              </ul>
            </motion.aside>
          </div>
        </section>

        <motion.section
          className="about-proof"
          variants={staggerChildren}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.22 }}
        >
          {milestones.map((item) => (
            <motion.article key={item.label} variants={revealUp}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
              <p>{item.copy}</p>
            </motion.article>
          ))}
        </motion.section>

        <motion.section
          className="about-journey"
          variants={staggerChildren}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <div className="about-section-heading">
            <p className="about-eyebrow">Designed around the whole journey</p>
            <h2>Useful before take-off, dependable while you are away.</h2>
          </div>
          <div className="about-journey__grid">
            {journeyStages.map((stage) => (
              <motion.article key={stage.number} variants={revealUp}>
                <span>{stage.number}</span>
                <h3>{stage.title}</h3>
                <p>{stage.copy}</p>
              </motion.article>
            ))}
          </div>
        </motion.section>

        <motion.section
          className="about-manifesto"
          variants={revealUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.25 }}
        >
          <div className="about-section-label">
            <Rocket size={18} />
            <span>Why We Exist</span>
          </div>
          <div className="about-manifesto__grid">
            <div>
              <h2>Travel tools should work together, not compete for attention.</h2>
            </div>
            <div>
              <p>
                Travellers still move between fragmented services, repeat the
                same information, and lose important details across platforms.
                We believe the experience can be clearer, faster, and easier to
                trust.
              </p>
              <p>
                Backed by builders who understand digital ecosystems, Teleport
                Now brings together the best of scale, speed, and user-centric
                design. We have built for fast-moving platforms before. Now we
                are applying that experience to travel.
              </p>
            </div>
          </div>
        </motion.section>

        <motion.section
          className="about-ambition"
          variants={revealUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.22 }}
        >
          <div className="about-ambition__content">
            <p className="about-eyebrow">Our ambition</p>
            <h2>To become the one app for all travel needs.</h2>
            <p>
              From connectivity to bookings, from airport experiences to
              in-destination services, Teleport Now is designed to eliminate
              friction and unify every part of the journey into a single,
              seamless platform.
            </p>
          </div>
          <div className="about-ecosystem" aria-label="Teleport Now travel ecosystem">
            {ecosystem.map((item, index) => (
              <span key={item} style={{ "--chip-index": index }}>
                {item}
              </span>
            ))}
          </div>
        </motion.section>

        <motion.section
          className="about-principles"
          variants={staggerChildren}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <div className="about-section-heading">
            <p className="about-eyebrow">How we build</p>
            <h2>Fast, compliant, and built for real journeys.</h2>
          </div>
          <div className="about-card-grid">
            {principles.map(({ icon: Icon, title, copy }) => (
              <motion.article key={title} variants={revealUp}>
                <div className="about-card-icon">
                  <Icon size={22} />
                </div>
                <h3>{title}</h3>
                <p>{copy}</p>
              </motion.article>
            ))}
          </div>
        </motion.section>

        <motion.section
          className="about-licenses"
          variants={revealUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.22 }}
        >
          <motion.div className="about-license-card" whileHover={{ y: -4 }}>
            <ShieldCheck size={34} />
            <div>
              <p className="about-eyebrow">Trust and compliance</p>
              <h2>Officially licensed under STB and IMDA.</h2>
              <p>
                Teleport Now is officially licensed under the Singapore Tourism
                Board and the Infocomm Media Development Authority, reinforcing
                our commitment to credibility as we scale globally.
              </p>
            </div>
          </motion.div>
          <div className="about-license-points">
            <span>
              <BadgeCheck size={18} /> Singapore Tourism Board
            </span>
            <span>
              <Building2 size={18} /> Infocomm Media Development Authority
            </span>
            <span>
              <Globe2 size={18} /> Global travel ecosystem
            </span>
          </div>
        </motion.section>

        <motion.section
          className="about-future"
          variants={revealUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.22 }}
        >
          <div>
            <p className="about-eyebrow">This is just the beginning</p>
            <h2>The future of travel will be built on speed, simplicity, and seamless experiences.</h2>
            <p>
              We are continuously expanding beyond traditional travel offerings,
              building a fully integrated ecosystem that connects technology,
              infrastructure, and real-world travel experiences into one
              unified platform.
            </p>
          </div>
          <div className="about-final-card">
            <img src="/images/Android.png" alt="" />
            <span>All you need to go.</span>
            <Link to="/download">
              Get Teleport Now <ArrowRight size={17} />
            </Link>
          </div>
        </motion.section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutUs;
